import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';

const LocationsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const locations = [
    {
      name: "Head Office - Jhansi",
      email: "enquiry@atozinfraspares.com",
      phone: "+91 94154 56760",
      isHeadOffice: true
    },
    {
      name: "Mahoba Office",
      contact: "Mr. Dharmendra Gupta",
      phone: "+91 99562 14141"
    },
    {
      name: "Lucknow Office",
      contact: "Mr. Vishwadeep Kanchan",
      phone: "+91 99356 47101"
    },
    {
      name: "Gwalior Office",
      contact: "Mr. Shravan Tiwari",
      phone: "+91 91196 02852"
    },
    {
      name: "Sagar Office",
      contact: "Mr. Raman Singh",
      phone: "+91 91196 02854"
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-gray-50">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-16 transform transition-all duration-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}>
            <div className="inline-block bg-green-100 text-green-500 px-4 py-2 rounded-full font-semibold mb-4">
              Our Locations
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Strategic Locations Across India
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              With presence across key industrial hubs, we ensure rapid delivery and support wherever you need us.
            </p>
          </div>

          {/* Locations Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-500 border-2 ${
                  location.isHeadOffice ? 'border-green-500' : 'border-gray-100 hover:border-green-500'
                } transform ${
                  isVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100 + 200}ms` }}
              >
                {/* Location Name */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {location.name}
                    </h3>
                    {location.isHeadOffice && (
                      <span className="inline-block bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full">
                        Head Office
                      </span>
                    )}
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-green-600" />
                  </div>
                </div>

                {/* Contact Person */}
                {location.contact && (
                  <p className="text-gray-600 text-sm mb-3">
                    <span className="font-medium">Contact:</span> {location.contact}
                  </p>
                )}

                {/* Contact Details */}
                <div className="space-y-2">
                  {location.email && (
                    <a 
                      href={`mailto:${location.email}`}
                      className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors text-sm"
                    >
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{location.email}</span>
                    </a>
                  )}
                  <a 
                    href={`tel:${location.phone.replace(/\s/g, '')}`}
                    className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors text-sm"
                  >
                    <Phone className="w-4 h-4 flex-shrink-0" />
                    <span>{location.phone}</span>
                  </a>
                </div>
              </div>
            ))}

            {/* Additional Locations Note */}
            <div
              className={`bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 shadow-md text-white flex flex-col justify-center items-center text-center transform ${
                isVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-10 opacity-0'
              }`}
              style={{ transitionDelay: '700ms' }}
            >
              <MapPin className="w-12 h-12 mb-3 opacity-80" />
              <h3 className="text-xl font-bold mb-2">3 Associate Partner Locations</h3>
              <p className="text-green-100 text-sm">
                Expanding our reach to serve you better across India
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LocationsSection;
